/**
 * Ticket photos (owner request 2026-10-11): the rules the picker applies before anything is sent.
 *
 * The server is the real gate (type, 15 MB, 5 per ticket or comment, EXIF stripped, private storage),
 * these only spare the user a round trip. HEIC is the one format the server cannot read, so it is
 * converted here when the browser can decode it (Safari) and refused with a plain message when it
 * cannot (Chrome, Firefox).
 */

/** Most photos on one new ticket or one comment. Mirrors TicketAttachmentLimits.MaxPerItem. */
export const MAX_TICKET_PHOTOS = 5

/** Largest photo the server accepts. Mirrors TicketAttachmentLimits.MaxUploadBytes. */
export const MAX_TICKET_PHOTO_BYTES = 15 * 1024 * 1024

/** Sent as they are; the server re-encodes them. */
const DIRECT_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'])

/** Long side of a photo this page converts or shrinks before sending. */
const CONVERT_MAX_DIMENSION = 2560

export const TICKET_PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.heic,.heif'

export function isHeic(file: Pick<File, 'type' | 'name'>): boolean {
  return /^image\/hei[cf]$/i.test(file.type) || /\.hei[cf]$/i.test(file.name)
}

/** Why a file cannot be offered at all, or null when it can (possibly after conversion). */
export function checkTicketPhoto(file: Pick<File, 'type' | 'name' | 'size'>): string | null {
  if (!DIRECT_TYPES.has(file.type.toLowerCase()) && !isHeic(file)) {
    return `${file.name || 'That file'} is not a photo. Use a JPEG, PNG, WebP or HEIC image.`
  }
  return null
}

/** Whether the file has to be re-encoded here before the server will take it. */
export function needsConversion(file: Pick<File, 'type' | 'name' | 'size'>): boolean {
  return isHeic(file) || file.size > MAX_TICKET_PHOTO_BYTES
}

/** How many more photos fit, given those already chosen. */
export function remainingPhotoSlots(chosen: number): number {
  return Math.max(0, MAX_TICKET_PHOTOS - chosen)
}

/**
 * The bytes to upload: the file itself, or a JPEG re-encode when it is HEIC or too large.
 * Throws an Error with a user-facing message when the browser cannot decode it.
 */
export async function prepareTicketPhoto(file: File): Promise<{ blob: Blob; name: string }> {
  const problem = checkTicketPhoto(file)
  if (problem) throw new Error(problem)
  if (!needsConversion(file)) return { blob: file, name: file.name || 'photo.jpg' }

  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch {
    throw new Error(
      isHeic(file)
        ? 'This browser cannot open HEIC photos. Save it as a JPEG first, or attach it from the app.'
        : `${file.name || 'That photo'} could not be read.`
    )
  }

  const scale = Math.min(1, CONVERT_MAX_DIMENSION / Math.max(bitmap.width, bitmap.height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(bitmap.width * scale)
  canvas.height = Math.round(bitmap.height * scale)
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('This browser could not prepare the photo.')
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  bitmap.close()

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.88))
  if (!blob) throw new Error('This browser could not prepare the photo.')
  if (blob.size > MAX_TICKET_PHOTO_BYTES) throw new Error('That photo is too large, even after shrinking it.')
  const base = (file.name || 'photo').replace(/\.[^.]+$/, '')
  return { blob, name: `${base}.jpg` }
}

/** The message to show for a failed upload or send: the server's own words when it gave any. */
export function ticketPhotoError(err: unknown, fallback: string): string {
  const e = err as { response?: { status?: number; data?: { error?: string; message?: string } }; message?: string }
  if (e?.response?.status === 503) return 'Photos cannot be attached right now.'
  return e?.response?.data?.error || e?.response?.data?.message || e?.message || fallback
}
